import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import { PopoverDirective } from '@epicuro-next/ataraxia/popover';
import { ProductImageMissingVisualConfig } from '@epicuro-next/ataraxia/product-image';
import { CellData } from '@epicuro-next/features/config-strip/utilities/materials';
import { getDeliveryShopId } from '@epicuro-next/utilities';
import {
  BasketAccountAddress,
  Product,
  ScaleSets,
} from 'graphql-api/gql-api-interfaces';
import { fromEvent } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { QuantityChangedPayload } from '../../types';
import { BaseAdvancedGridComponent } from '../base-advanced-grid/base-advanced-grid.component';

interface CopyCoordinates {
  from: { x: number; y: number };
  to: { x: number; y: number };
}

@Component({
  selector: 'epc-advanced-size-grid',
  templateUrl: './advanced-size-grid.component.html',
  styleUrls: ['./advanced-size-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AdvancedSizeGridComponent extends BaseAdvancedGridComponent {
  @Input() public products: Product[];
  @Input() public availableScaleSets: ScaleSets;
  // @ts-ignore
  @Input() public sizesInventories: unknown;
  @Input() public disabledMaterialsIds: {
    [key: string]: { hasNow: boolean; dates: string[]; future: string };
  } = {};

  @Input()
  public isNowOrder = false;

  @Input()
  public productImageMissingVisualConfig?: ProductImageMissingVisualConfig;

  @Output() public sizeSelect = new EventEmitter<Partial<CellData>>();

  @Output() public shopSelect = new EventEmitter<{
    accountId: string;
    addressId: string;
  }>();

  @Output() public cellCleared = new EventEmitter();

  public triggerEvent = new Event('manual');
  public imageMaxSizes = IMAGE_MAX_SIZES;

  public readonly displaySizesModeLabel = $localize`:Config Strip|Label for displayBy sizes toggle@@displayByToggleSizes:Sizes`;

  @ViewChild('sizeGrid') public sizeGrid: ElementRef;

  public getQuantity(account: BasketAccountAddress, product: Product) {
    const accountId = account.accountId + '-' + account.addressId;
    const shopDeliveryItems = this.deliveryItems[accountId] ?? {};
    return (
      product.children
        ?.filter(({ productId }) => !!shopDeliveryItems[productId])
        .reduce(
          (acc, { productId }) => acc + shopDeliveryItems[productId].quantity,
          0,
        ) ?? 0
    );
  }

  public onKeydown(event: KeyboardEvent) {
    if (event.code !== 'Tab') {
      event.preventDefault();
    }
  }

  public onCopy({ from, to }: CopyCoordinates) {
    const row = this.products[from.y];
    const column = this.shipTos[from.x];

    const xDelta = from.x < to.x ? 1 : -1;
    const yDelta = from.y < to.y ? 1 : -1;
    const rows = [];
    for (
      let rowIndex = from.y;
      yDelta > 0 ? rowIndex <= to.y : rowIndex >= to.y;
      rowIndex += yDelta
    ) {
      rows.push(this.products[rowIndex].productId);
    }
    const columns = [];
    for (
      let colIndex = from.x;
      xDelta > 0 ? colIndex <= to.x : colIndex >= to.x;
      colIndex += xDelta
    ) {
      columns.push(
        getDeliveryShopId({
          accountId: this.shipTos[colIndex].accountId,
          addressId: this.shipTos[colIndex].addressId,
        }),
      );
    }
    const copyPayload = {
      from: {
        productId: row.productId,
        shopId: getDeliveryShopId({
          accountId: column.accountId,
          addressId: column.addressId,
        }),
      },
      to: { shops: columns, products: rows },
    };
    this.copyQuantities.emit(copyPayload);
  }

  public openSizeGrid(
    popoverRef: PopoverDirective,
    account: BasketAccountAddress,
    cellDisabled: boolean,
  ) {
    if (cellDisabled) return;

    this.shopSelect.emit({
      accountId: account.accountId,
      addressId: account.addressId,
    });
    popoverRef.open();

    requestAnimationFrame(() => {
      fromEvent(window, 'keyup')
        .pipe(
          filter(
            (event: KeyboardEvent) =>
              event.key === 'Enter' || event.key === 'Escape',
          ),
          take(1),
          takeUntil(popoverRef.atxPopoverClose),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          popoverRef.close();
        });
    });
  }

  public getMaterialTitle(material: Product): string {
    return this.titleAttributes
      ?.map((att) =>
        att.split('.').reduce((ac: { [x: string]: unknown }, c: string) => {
          return ac[c];
        }, material),
      )
      .filter((item) => !!item)
      .join(' ');
  }

  public getDeliveryItems(account: BasketAccountAddress) {
    const accountId = account.accountId + '-' + account.addressId;
    return {
      items: (this.deliveryItems ?? {})[accountId] ?? {},
    };
  }

  public quantityChange(
    changes: QuantityChangedPayload[],
    shop: BasketAccountAddress,
  ) {
    this.quantityChanged.emit({ changes, shop });
  }

  public onPopoverClose({ referer }: { referer: ViewContainerRef }) {
    referer.element.nativeElement.focus();
  }

  public isProductUnavailable(product: Product) {
    return product;
  }

  public clearCell(product: Product, shop: BasketAccountAddress) {
    const changes =
      product.children?.map(({ productId }) => ({ productId, quantity: 0 })) ??
      [];
    this.cellCleared.emit({ changes, shop });
  }
}
